import React from 'react';
import { CheckCircle, Award, Clock, Users } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';

const AboutPage = () => {
  const stats = [
    { label: 'Years of Experience', value: '20+', icon: Clock },
    { label: 'Integrations', value: '50+', icon: Users },
    { label: 'Tough Problems Solved', value: '1000+', icon: CheckCircle },
  ];

  const values = [
    {
      title: 'Customer Focus',
      description: 'Our clients are at the center of everything we do. Their success is our primary goal.',
    },
    {
      title: 'Integrity',
      description: 'We conduct business with honesty, transparency, and ethical practices in all our relationships.',
    },
    {
      title: 'Innovation',
      description: 'We embrace innovation and stay ahead of industry trends to provide cutting-edge solutions.',
    },
    {
      title: 'Excellence',
      description: 'We strive for excellence in everything we do, delivering quality results that exceed expectations.',
    },
  ];

  return (
    <>
      <Navbar showTestimonials={false} />
      <section className="py-20 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              About Us
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              How we came to be, our journey, and why we do what we do.
            </p>
          </div>

          {/* Main Content */}
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
            {/* Content */}
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Our Story
              </h3>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  We started Granite State Cloud Solutions with a simple mission:
                  To empower small and midsize businesses with the cloud tools and expertise
                  typically reserved for Fortune 500 companies.  We recognized that
                  competition is what breeds innovation, and we wanted to level the playing field
                  for businesses of all sizes.
                </p>
                <p>
                  With the rapid evolution of AI and Cloud technologies, the need for small
                  and midsize businesses to adopt this technology has never been greater, but
                  the resources and expertise to do so are often out of reach.
                  GSCS was founded to bridge this gap, providing tailored solutions that
                  meet the unique needs of each client, no matter their size or industry.
                </p>
                <p>
                  Our team is composed of seasoned professionals with decades of combined experience
                  in cloud computing, AI, and process automation. We are passionate about helping
                  businesses thrive in the digital age, and we take pride in delivering solutions
                  that drive real results for those we serve.
                </p>
              </div>

              <div className="mt-8 space-y-3">
                <div className="flex items-center text-gray-700">
                  <CheckCircle className="h-5 w-5 text-primary-600 mr-3" />
                  <span>Experienced industry professionals</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <CheckCircle className="h-5 w-5 text-primary-600 mr-3" />
                  <span>Proven track record of success</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <CheckCircle className="h-5 w-5 text-primary-600 mr-3" />
                  <span>Dedicated customer support</span>
                </div>
              </div>
            </div>

            {/* Datacenter Image */}
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src="/datacenter.png"
                alt="Modern data center infrastructure representing our cloud solutions"
                className="w-full h-96 object-cover"
              />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 max-w-4xl mx-auto">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div key={index} className="text-center bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-shadow duration-300">
                  <div className="flex items-center justify-center w-16 h-16 bg-primary-100 rounded-xl mx-auto mb-4">
                    <IconComponent className="h-8 w-8 text-primary-600" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-gray-600 font-medium">
                    {stat.label}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Values */}
          <div>
            <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">
              Our Core Values
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div key={index} className="text-center bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-shadow duration-300">
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">
                    {value.title}
                  </h4>
                  <p className="text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <Footer showTestimonials={false} />
    </>
  );
};

export default AboutPage;
